// SPDX-FileCopyrightText: 2022-2026 Lablup Inc. All rights reserved.
// SPDX-License-Identifier: LicenseRef-Proprietary
import i18n from "i18next";
import { atom } from "jotai";
import { atomFamily } from "jotai/utils";

import { logApiError } from "../../infra/apiError";
import { showDedupedErrorToast } from "../../infra/dedupedToast";
import { fetchJson } from "../../infra/fetchJson";
import type { VFolderHostListInfo, VirtualFolder } from "../backendai";

export const hostListInfoAtom = atom(async (get) => {
  try {
    return await fetchJson<VFolderHostListInfo>("/func/folders/_/hosts");
  } catch (e) {
    logApiError("hostListInfoAtom:/func/folders/_/hosts", e);
    showDedupedErrorToast(
      "hostList:load",
      i18n.t("error.FailedToLoadHostInfo")
    );
    return {
      allowed: [],
      default: null,
      volume_info: {},
    } as VFolderHostListInfo;
  }
});

export const folderAtom = atomFamily(
  ({ vFolderName, vFolderId }: { vFolderName: string; vFolderId?: string }) =>
    atom(async (get) => {
      // Prefer the stable vfolder UUID (collision-safe across viewer scopes);
      // fall back to the name for callers that only have it. The request path
      // and the atomFamily key below both use this same ref.
      const ref = vFolderId ?? vFolderName;
      if (!ref) return null;
      try {
        return await fetchJson<VirtualFolder>(
          `/func/folders/${encodeURIComponent(ref)}`
        );
      } catch (e) {
        if (
          e &&
          typeof e === "object" &&
          "status" in e &&
          (e as { status: number }).status === 404
        ) {
          return null;
        }
        // Non-404 errors (e.g. 429 on rate-limited backends, 5xx during
        // outages) must not tear down consumers via Suspense/ErrorBoundary.
        // Log, surface a single toast via antd's static message API, and
        // degrade to `null` — symmetric with the 404 path, which lets
        // callers render the plain folder name fallback. See issue #3382.
        logApiError(`folderAtom:/func/folders/${ref}`, e);
        showDedupedErrorToast(
          `folder:${ref}`,
          i18n.t("error.FailedToLoadFolder")
        );
        return null;
      }
    }),
  (a, b) => (a.vFolderId ?? a.vFolderName) === (b.vFolderId ?? b.vFolderName)
);
